import { useSupabaseQuery } from './useSupabaseQuery';
import { useSupabaseMutation } from './useSupabaseMutation';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { queryKeys } from '@/lib/query-client';
import { BOOKING_STATUS, BookingStatus } from '@/lib/booking-status';

/**
 * 결제용 예약 참가자 정보 타입
 */
export interface BookingParticipant {
  name: string;
  phone: string;
  seat?: number | null;
}

/**
 * 결제용 예약 데이터 타입
 */
export interface BookingForPayment {
  booking_id: number;
  user_id: string;
  status: BookingStatus;
  total_price: number;
  num_participants: number;
  trips: {
    departure_date: string;
    departure_point?: string | null;
    products: {
      name: string;
    };
  };
  participants: BookingParticipant[];
}

/**
 * 예약 상태 업데이트를 위한 변수 타입
 */
export interface UpdateBookingForPaymentVariables {
  bookingId: number;
  orderId: string;
  status: BookingStatus;
}

/**
 * 결제용 예약 정보 조회 훅
 * 예약 정보와 참가자 정보를 함께 조회합니다.
 * 
 * @param bookingId - 예약 ID
 * @param userId - 사용자 ID (보안 검증용)
 * @returns 예약 정보와 참가자 정보가 포함된 데이터
 */
export function useBookingForPayment(bookingId: string, userId?: string) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseQuery(
    queryKeys.payments.detail(bookingId),
    async ({ signal }) => {
      // 1. 예약 정보와 여행, 상품 정보 조회
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select(`
          *,
          trips!inner(
            *,
            products!inner(*)
          )
        `)
        .eq('booking_id', parseInt(bookingId))
        .single()
        .abortSignal(signal);

      if (bookingError) {
        // AbortError는 React StrictMode나 컴포넌트 재마운트로 인한 정상적인 동작이므로 무시
        if (bookingError.message?.includes('AbortError')) {
          throw bookingError; // React Query가 자동으로 재시도함
        }
        
        // bookingError에 사용자 정의 메시지를 추가하여 throw
        bookingError.message = '예약 정보를 찾을 수 없습니다: ' + bookingError.message;
        throw bookingError;
      }

      // 사용자 소유권 검증
      if (userId && booking.user_id !== userId) {
        throw new Error('접근 권한이 없습니다. 본인의 예약만 조회할 수 있습니다.');
      }

      // 2. 참가자 정보 조회
      const { data: participants, error: participantsError } = await supabase
        .from('booking_participants')
        .select('name, phone, seat')
        .eq('booking_id', booking.booking_id)
        .order('participant_order', { ascending: true })
        .abortSignal(signal);

      if (participantsError) {
        console.warn('참가자 정보 조회 실패 (진행은 계속됨):', participantsError);
        // 참가자 정보 오류는 경고만 하고 빈 배열로 처리
      }

      // 데이터 구조 정리 및 반환
      return {
        ...booking,
        participants: participants || [],
      } as BookingForPayment;
    },
    {
      enabled: !!bookingId && !isNaN(parseInt(bookingId)),
      staleTime: 0, // 결제 화면에서는 항상 최신 데이터 필요
      gcTime: 5 * 60 * 1000, // 5분간 캐시 유지
      retry: (failureCount, error) => {
        // AbortError는 재시도하지 않음
        if (error instanceof Error && error.name === 'AbortError') {
          return false;
        }
        // 권한 오류도 재시도하지 않음
        if (error instanceof Error && error.message.includes('접근 권한')) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    }
  );
}

/**
 * 결제용 예약 상태 업데이트 훅
 * 예약 상태와 주문 ID를 업데이트합니다.
 * 
 * @returns 뮤테이션 객체
 */
export function useUpdateBookingForPayment() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation(
    async (variables: UpdateBookingForPaymentVariables) => {
      const { bookingId, orderId, status } = variables;

      // 상태 전이 검증 (optional - 서버에서도 검증하지만 UX 향상을 위해)
      if (status === BOOKING_STATUS.PENDING) {
        // pending으로 업데이트하는 경우는 결제 진행 시작을 의미
      }

      const { data, error } = await supabase
        .from('bookings')
        .update({
          order_id: orderId,
          status: status,
          updated_at: new Date().toISOString(),
        })
        .eq('booking_id', bookingId)
        .select()
        .single();

      return { data, error };
    },
    {
      // 관련 쿼리 무효화
      invalidateQueries: [
        queryKeys.reservations.all(),
        queryKeys.payments.all(),
      ],
      onSuccess: (data, variables) => {
        console.log('Booking updated successfully:', {
          bookingId: variables.bookingId,
          orderId: variables.orderId,
          status: variables.status,
        });
      },
      onError: (error, variables) => {
        console.error('Failed to update booking:', error, variables);
      },
    }
  );
}

/**
 * 결제 완료 후 예약 확정을 위한 훅
 * 주로 결제 성공 페이지에서 사용됩니다.
 * 
 * @returns 뮤테이션 객체
 */
// 예약 업데이트 데이터 타입 정의
interface BookingUpdateData {
  status: BookingStatus;
  updated_at: string;
  payment_key?: string;
}

export function useConfirmBookingPayment() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation(
    async (variables: { bookingId: number; paymentKey?: string }) => {
      const { bookingId, paymentKey } = variables;

      const updateData: BookingUpdateData = {
        status: BOOKING_STATUS.CONFIRMED,
        updated_at: new Date().toISOString(),
      };

      // 결제 키가 있으면 추가 저장
      if (paymentKey) {
        updateData.payment_key = paymentKey;
      }

      const { data, error } = await supabase
        .from('bookings')
        .update(updateData)
        .eq('booking_id', bookingId)
        .select()
        .single();

      return { data, error };
    },
    {
      invalidateQueries: [
        queryKeys.reservations.all(),
        queryKeys.payments.all(),
      ],
      onSuccess: (data, variables) => {
        console.log('Booking confirmed successfully:', variables.bookingId);
      },
    }
  );
}

/**
 * 결제 실패 시 예약 상태 복구를 위한 훅
 * 
 * @returns 뮤테이션 객체
 */
export function useRevertBookingPayment() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation(
    async (bookingId: number) => {
      const { data, error } = await supabase
        .from('bookings')
        .update({
          status: BOOKING_STATUS.PENDING,
          order_id: null, // 주문 ID 초기화
          updated_at: new Date().toISOString(),
        })
        .eq('booking_id', bookingId)
        .select()
        .single();

      return { data, error };
    },
    {
      invalidateQueries: [
        queryKeys.reservations.all(),
        queryKeys.payments.all(),
      ],
      onSuccess: (data, bookingId) => {
        console.log('Booking reverted successfully:', bookingId);
      },
    }
  );
}